import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';

const IMAGE_WIDTH_CONTAIN_THRESHOLD = 1000;
const IMAGE_WIDTH_INHERIT_THRESHOLD = 160;

export interface ChallengeCardData {
  id: string;
  name: string;
  description?: string;
  /** Subtitle from API, shown below title (replaces provider/NHA Team). */
  provider?: string;
  imageUrl?: string;
  participants: number;
  submissions: number;
  prizeMoney: number;
  /** Currency code from API (e.g. INR, USD). When INR, $ is not shown. */
  currency?: string;
  daysRemaining: number;
  /** ISO date string of submission deadline. Used to show "Xhr left" when deadline is today. */
  endDate?: string;
  /** Submission start date (YYYY-MM-DD). */
  submissionStartsAt?: string;
  /** Submission end date (YYYY-MM-DD). */
  submissionEndsAt?: string;
  link?: string;
}

@Component({
  selector: 'app-challenge-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './challenge-card.component.html',
  styleUrl: './challenge-card.component.scss',
})
export class ChallengeCardComponent implements OnChanges {
  @Input() data!: ChallengeCardData;
  @Input() variant: 'default' | 'alternative' | 'minimal' = 'minimal'; // Default to minimal
  @Input() showDescription: boolean = false; // Control description visibility
  @Input() disableNavigation: boolean = false; // Disable click navigation
  /** Override object-fit. When not set, uses width>1000px → contain, else → cover. */
  @Input() imageObjectFit?: 'cover' | 'contain';

  /** Image natural width, set on load. Used for auto object-fit. */
  imageWidth: number | null = null;

  private googleAnalytics = inject(GoogleAnalyticsService);

  /** Use contain when: explicit 'contain' OR (no override and image width > 1000px). */
  get useImageContain(): boolean {
    if (this.imageObjectFit === 'cover') return false;
    if (this.imageObjectFit === 'contain') return true;
    if (this.useImageInherit) return false;
    return this.imageWidth != null && this.imageWidth > IMAGE_WIDTH_CONTAIN_THRESHOLD;
  }

  /** Use inherit when: no override and image width < 160px. */
  get useImageInherit(): boolean {
    if (this.imageObjectFit) return false;
    return this.imageWidth != null && this.imageWidth < IMAGE_WIDTH_INHERIT_THRESHOLD;
  }

  /** True when currency is USD (show $ icon). Hide $ icon for INR. */
  get isUsdCurrency(): boolean {
    return this.data?.currency?.toUpperCase() === 'USD';
  }

  /** True when showing hours (< 24hr) – for urgent deadline highlight. */
  get isHoursLeft(): boolean {
    const h = this.getHoursLeft();
    return h != null && h > 0 && h < 24;
  }

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']?.currentValue?.imageUrl !== changes['data']?.previousValue?.imageUrl) {
      this.imageWidth = null;
    }
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img?.naturalWidth) {
      this.imageWidth = img.naturalWidth;
    }
  }

  /** Currency symbol for prize (₹ or $). */
  getPrizeSymbol(): string {
    const currency = this.data?.currency?.toUpperCase();
    if (currency === 'USD') return '$';
    return '₹';
  }

  /** Formatted prize amount (number only, no symbol). */
  getPrizeAmount(amount: number): string {
    return amount.toLocaleString('en-IN');
  }

  formatPrizeMoney(amount: number): string {
    return `${this.getPrizeSymbol()}${this.getPrizeAmount(amount)}`;
  }

  formatDaysRemaining(days: number): string {
    const hours = this.getHoursLeft();
    if (hours != null && hours > 0 && hours < 24) {
      return hours === 1 ? '1hr left' : `${hours}hr left`;
    }
    if (days === 0) return 'Deadline today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  }

  /** Hours until endDate. Returns null if no endDate or already passed. */
  private getHoursLeft(): number | null {
    const end = this.data?.endDate;
    if (!end || typeof end !== 'string') return null;

    // API often returns date-only (YYYY-MM-DD). Treat as end of that day in local time.
    const trimmed = end.trim();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
    const endTime = isDateOnly
      ? new Date(trimmed + 'T23:59:59.999').getTime()
      : new Date(end).getTime();

    const now = Date.now();
    if (endTime <= now) return null;
    const hours = Math.ceil((endTime - now) / (1000 * 60 * 60));
    return hours;
  }

  getImageUrl(): string {
    // Use provided image URL or default placeholder
    return this.data.imageUrl || 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&h=200&fit=crop';
  }

  /** Format date for submission display (11th Feb 26). */
  formatDisplayDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = String(d.getFullYear()).slice(-2);
    return `${day}${suffix} ${month} ${year}`;
  }

  /** Tooltip explaining what the deadline countdown means */
  getDeadlineTooltip(): string {
    if (this.data.daysRemaining > 0) {
      return 'Submission deadline – time remaining to submit your solution to this challenge';
    }
    if (this.data.daysRemaining === 0) {
      return 'Submission deadline is today – last day to submit your solution';
    }
    return 'This challenge has ended; results may be available on the details page';
  }

  navigateToDetail(): void {
    if (this.data.id) {
      this.googleAnalytics.trackChallengeClick(this.data.name ?? '', this.data.id);
      this.router.navigate(['/challenges', this.data.id]);
    }
  }
}

